/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

// import { Modal, ModalBody, ModalHeader, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { Module } from '@msdyn365-commerce-modules/utilities';

import { IAccountLoyaltyProps, layout as layoutNames } from '../definition-extensions/account-loyalty.ext.props.autogenerated';
import { IAccountLoyaltyViewProps } from '@msdyn365-commerce-modules/account-management';
import { ILoyaltySignUpProps } from '@msdyn365-commerce-modules/account-management';

import { Row, Container } from 'reactstrap';
import classnames from 'classnames';
import { saveLoyaltyDetails } from '../utilities/datalayer/helpers';

const isWideOrCompactLayout = (layout: layoutNames) => {
    return [layoutNames.wideLayout, layoutNames.compact].includes(layout);
};

const layoutClassNames = {
    [layoutNames.wideLayout]: 'loyalty-wide-layout',
    [layoutNames.compact]: 'loyalty-compact-layout'
};
// @ts-ignore
const getLayoutClassName = (layout: layoutNames) => layoutClassNames[layout] || 'loyalty-wide-layout';

const AccountLoyaltyView: React.FC<IAccountLoyaltyViewProps & IAccountLoyaltyProps<{}> & ILoyaltySignUpProps<{}>> = props => {
    const { AccountLoyalty, heading, cardNumber, joinDate, points } = props;

    const {
        layout,
        displayJoinDate,
        displayAppleWallet,
        displayGoogleWallet,
        appleWalletBtnImage,
        googleWalletBtnImage,
        pointsExpirationReminder,
        loyaltyTiersLink,
        walletLoyaltyProgramName
    } = props.config;

    const {
        loyaltyTierLabel,
        loyaltyCardLabel,
        loyaltyCardLabel_CL,
        availablePointsLabel,
        availablePointsLabel_CL_before,
        availablePointsLabel_CL_after,
        viewLoyaltyPageText
    } = props.resources;

    const firstLoyaltyGroup =
        // @ts-ignore
        props.AccountLoyalty.moduleProps.data.loyaltyCard.result &&
        // @ts-ignore
        props.AccountLoyalty.moduleProps.data.loyaltyCard.result.LoyaltyGroups[0];
    // @ts-ignore
    const highestTierId = firstLoyaltyGroup.HighestActiveLoyaltyCardTier;
    // @ts-ignore
    const tierDetails = firstLoyaltyGroup.LoyaltyCardTiers.filter(tier => tier.RecordId === highestTierId)[0];
    const tierName = tierDetails.TierId;

    const user_email = props.AccountLoyalty.moduleProps.context.request.user.emailAddress;
    // @ts-ignore
    const user_card_number = cardNumber!.props.text;

    let points_numbers = [-1.1];

    // @ts-ignore
    if (points && points.props) {
        // @ts-ignore
        points_numbers = points.props.text.split('.');
    }

    const height_to_set = 48; // Minimum per Google guidelines

    const tierClass = `tier-name__${tierName}`;

    const apple_wallet_check: boolean =
        displayAppleWallet && appleWalletBtnImage && appleWalletBtnImage.src && appleWalletBtnImage.altText && user_card_number;
    const google_wallet_check: boolean =
        displayGoogleWallet && googleWalletBtnImage && googleWalletBtnImage.src && googleWalletBtnImage.altText && user_card_number;
    const programString: string = walletLoyaltyProgramName || 'v-club-emea';

    saveLoyaltyDetails({
        loyaltyTierId: tierName,
        vclub_points: points_numbers[0] != -1.1 ? points_numbers[0] : 0,
        programName: programString
    });

    if (isWideOrCompactLayout(layout)) {
        // Wide or compact layout (Loyalty page and Homepage row)
        return (
            <Module {...AccountLoyalty} className={getLayoutClassName(layout)}>
                {heading}
                <Container className={AccountLoyalty.className}>
                    <Row>
                        <div className={'col col-12 col-sm-4 main-col'}>
                            <h2>{loyaltyCardLabel}</h2>
                            {cardNumber}
                            {(apple_wallet_check || google_wallet_check) && (
                                <div className='ms-account-loyalty__wallet-div'>
                                    {apple_wallet_check ? (
                                        <a
                                            href={`https://func-applewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${user_card_number}&email=${user_email}`}
                                        >
                                            <img
                                                className='ms-account-loyalty__wallet-img'
                                                src={appleWalletBtnImage?.src}
                                                alt={appleWalletBtnImage?.altText}
                                                height={height_to_set}
                                            ></img>
                                        </a>
                                    ) : null}
                                    {google_wallet_check ? (
                                        <a
                                            href={`https://func-googlewallet-prod.azurewebsites.net/venchiloyalty/v1/passes/getpass?program=${programString}&loyaltyCardNumber=${user_card_number}&email=${user_email}`}
                                        >
                                            <img
                                                className='ms-account-loyalty__wallet-img'
                                                src={googleWalletBtnImage?.src}
                                                alt={googleWalletBtnImage?.altText}
                                                height={height_to_set}
                                            ></img>
                                        </a>
                                    ) : null}
                                </div>
                            )}
                            {/* @ts-ignore */}
                            {displayJoinDate && !joinDate?.props.text.endsWith('1900') ? joinDate : null}
                        </div>
                        <div className={'col col-12 col-sm-4 main-col'}>
                            <h2>{availablePointsLabel}</h2>
                            {points_numbers[0] != -1.1 ? <p className='ms-account-loyalty__total-points'>{points_numbers[0]}</p> : null}
                            {pointsExpirationReminder ? (
                                <p className='ms-account-loyalty__points-exp-reminder'>{pointsExpirationReminder}</p>
                            ) : null}
                            {viewLoyaltyPageText ? <a href='https://venchi.com/loyalty'>{viewLoyaltyPageText}</a> : null}
                        </div>
                        <div className={'col col-12 col-sm-4 main-col'}>
                            <h2 className='tier-label'>{loyaltyTierLabel}</h2>
                            <p className={classnames('tier-name', tierClass)}>
                                <span className='tier-container'>{tierName}</span>
                            </p>
                            {loyaltyTiersLink &&
                            loyaltyTiersLink.linkUrl &&
                            loyaltyTiersLink.linkUrl.destinationUrl &&
                            loyaltyTiersLink.linkText ? (
                                <a href={loyaltyTiersLink.linkUrl.destinationUrl}>{loyaltyTiersLink.linkText}</a>
                            ) : null}
                        </div>
                    </Row>
                </Container>
            </Module>
        );
    }

    // Card layout (Homepage column)
    return (
        <Module {...AccountLoyalty} className={classnames(AccountLoyalty.className, 'loyalty-card-layout')}>
            {heading}
            <div className={'ms-account-loyalty__main'}>
                <Container>
                    <Row>
                        <div className='col col-12 col-xl-6 loyalty-col'>
                            <h3 className='loyalty-label'>{loyaltyCardLabel_CL}</h3>
                            <div className='inline-div'>
                                &nbsp;<p>{cardNumber}</p>
                            </div>
                        </div>
                        <div className='col col-12 col-xl-6 loyalty-col'>
                            <h3 className='loyalty-label'>{loyaltyTierLabel}</h3>
                            <p className={classnames('tier-name', tierClass)}>
                                &nbsp;<span className='tier-container'>{tierName}</span>
                            </p>
                        </div>
                    </Row>
                    <Row>
                        <div className='col col-12 loyalty-col__points'>
                            <h3 className='loyalty-label'>
                                <span className='h3-doppelganger'>
                                    {availablePointsLabel_CL_before}&nbsp;
                                    {points_numbers[0] != -1.1 ? (
                                        <p className='ms-account-loyalty__total-points'>{points_numbers[0]}</p>
                                    ) : null}
                                    &nbsp;{availablePointsLabel_CL_after}
                                </span>
                            </h3>
                        </div>
                    </Row>
                </Container>
            </div>
        </Module>
    );
};

export default AccountLoyaltyView;
